import { createSlice } from '@reduxjs/toolkit';

import { SurveyServiceClient } from '../common/services/httpClient';
import {
  fetchSurveyResponsesPath,
  nextQuestionPath,
  submitAnswerPath,
  resetAnswersPath,
  postResponsesPath,
  submitAnswerBatchPath,
  previousQuestionPath,
} from './common/services/urlService';
import httpAction from './httpAction';

export const SURVEY_REDUCER = 'survey/';

export const isSurveyComplete = (nextAnswer) => !nextAnswer;

export const initialState = {
  fetchQuestionStatus: {
    pending: false,
    success: false,
    error: false,
  },
  postAnswerStatus: {
    pending: false,
    success: false,
    error: false,
  },
  putAnswerStatus: {
    pending: false,
    success: false,
    error: false,
  },
  submitAnswerStatus: {
    pending: false,
    success: false,
    error: false,
  },
  fetchSurveyResponsesStatus: {
    pending: false,
    success: false,
    error: false,
  },
  resetAnswersStatus: {
    pending: false,
    success: false,
    error: false,
  },
  postResponsesStatus: {
    pending: false,
    success: false,
    error: false,
  },
  question: null,
  surveyComplete: false,
  surveyResponses: null,
};

export const reducers = {
  fetchQuestionBegan: (state) => {
    return {
      ...state,
      fetchQuestionStatus: {
        pending: true,
        success: false,
        error: false,
      },
    };
  },
  fetchQuestionSuccess: (state, { payload: question }) => {
    return {
      ...state,
      fetchQuestionStatus: {
        pending: false,
        success: true,
        error: false,
      },
      question,
      surveyComplete: isSurveyComplete(question),
    };
  },
  fetchQuestionError: (state, { payload: error }) => {
    return {
      ...state,
      fetchQuestionStatus: {
        pending: false,
        success: false,
        error,
      },
    };
  },
  postAnswerBegan: (state) => {
    return {
      ...state,
      postAnswerStatus: {
        pending: true,
        success: false,
        error: false,
      },
      submitAnswerStatus: {
        pending: true,
        success: false,
        error: false,
      },
    };
  },
  postAnswerSuccess: (state) => {
    return {
      ...state,
      postAnswerStatus: {
        pending: false,
        success: true,
        error: false,
      },
      submitAnswerStatus: {
        pending: false,
        success: true,
        error: false,
      },
    };
  },
  postAnswerError: (state, { payload: error }) => {
    return {
      ...state,
      postAnswerStatus: {
        pending: false,
        success: false,
        error,
      },
      submitAnswerStatus: {
        pending: false,
        success: false,
        error,
      },
    };
  },
  putAnswerBegan: (state) => {
    return {
      ...state,
      putAnswerStatus: {
        pending: true,
        success: false,
        error: false,
      },
      submitAnswerStatus: {
        pending: true,
        success: false,
        error: false,
      },
    };
  },
  putAnswerSuccess: (state) => {
    return {
      ...state,
      putAnswerStatus: {
        pending: false,
        success: true,
        error: false,
      },
      submitAnswerStatus: {
        pending: false,
        success: true,
        error: false,
      },
    };
  },
  putAnswerError: (state, { payload: error }) => {
    return {
      ...state,
      putAnswerStatus: {
        pending: false,
        success: false,
        error,
      },
      submitAnswerStatus: {
        pending: false,
        success: false,
        error,
      },
    };
  },
  resetSubmitAnswerStatus: (state) => {
    return {
      ...state,
      submitAnswerStatus: {
        pending: false,
        success: false,
        error: false,
      },
    };
  },
  fetchSurveyResponsesBegan: (state) => {
    return {
      ...state,
      fetchSurveyResponsesStatus: {
        pending: true,
        success: false,
        error: false,
      },
    };
  },
  fetchSurveyResponsesSuccess: (state, { payload: surveyResponses }) => {
    return {
      ...state,
      fetchSurveyResponsesStatus: {
        pending: false,
        success: true,
        error: false,
      },
      surveyResponses,
    };
  },
  fetchSurveyResponsesError: (state, { payload: error }) => {
    return {
      ...state,
      fetchSurveyResponsesStatus: {
        pending: false,
        success: false,
        error,
      },
    };
  },
  resetAnswersBegan: (state) => {
    return {
      ...state,
      resetAnswersStatus: {
        pending: true,
        success: false,
        error: false,
      },
    };
  },
  resetAnswersSuccess: (state) => {
    return {
      ...state,
      resetAnswersStatus: {
        pending: false,
        success: true,
        error: false,
      },
      question: null,
      surveyComplete: false,
      surveyResponses: null,
    };
  },
  resetAnswersError: (state, { payload: error }) => {
    return {
      ...state,
      resetAnswersStatus: {
        pending: false,
        success: false,
        error,
      },
    };
  },
  postResponsesBegan: (state) => {
    return {
      ...state,
      postResponsesStatus: {
        pending: true,
        success: false,
        error: false,
      },
    };
  },
  postResponsesSuccess: (state) => {
    return {
      ...state,
      postResponsesStatus: {
        pending: false,
        success: true,
        error: false,
      },
    };
  },
  postResponsesError: (state, { payload: error }) => {
    return {
      ...state,
      postResponsesStatus: {
        pending: false,
        success: false,
        error,
      },
    };
  },
  resetFetchSurveyResponsesStatus: (state) => {
    return {
      ...state,
      fetchSurveyResponsesStatus: {
        pending: false,
        success: false,
        error: false,
      },
    };
  },
  getQuestionById: (state, { payload: questionId }) => {
    const { surveyResponses } = state;
    const questions = surveyResponses && surveyResponses.questions;
    const question = questions && questions.find((q) => q.id === +questionId);
    return {
      ...state,
      question,
    };
  },
  resetFetchQuestionStatus: (state) => {
    return {
      ...state,
      fetchQuestionStatus: {
        pending: false,
        success: false,
        error: false,
      },
    };
  },
  resetQuestion: (state) => {
    return {
      ...state,
      question: null,
    };
  },
  resetPostResponsesStatus: (state) => {
    return {
      ...state,
      postResponsesStatus: {
        pending: false,
        success: false,
        error: false,
      },
    };
  },
  resetResetAnswersStatus: (state) => {
    return {
      ...state,
      resetAnswersStatus: {
        pending: false,
        success: false,
        error: false,
      },
    };
  },
};

export const setupSurveySlice = () => {
  const surveySlice = createSlice({
    name: 'survey',
    initialState,
    reducers,
  });
  return surveySlice;
};

const { reducer, actions } = setupSurveySlice();

export const {
  fetchQuestionBegan: getQuestionBegan,
  fetchQuestionSuccess: getQuestionSuccess,
  fetchQuestionError: getQuestionError,
  postAnswerBegan,
  postAnswerSuccess,
  postAnswerError,
  resetSubmitAnswerStatus,
  resetFetchSurveyResponsesStatus,
  getQuestionById,
  resetFetchQuestionStatus,
  resetQuestion,
  resetPostResponsesStatus,
  resetResetAnswersStatus,
} = actions;

// httpActions
export const fetchNextQuestion = (projectId, orderNumber = null) =>
  httpAction({
    reducer: `${SURVEY_REDUCER}fetchQuestion`,
    endpoint: nextQuestionPath(projectId, orderNumber),
    verb: 'GET',
    httpClient: SurveyServiceClient(projectId),
  });

export const postAnswer = (projectId, answer) => {
  const body = answer.length
    ? answer.map((ans) => ({
        projectId,
        ...ans,
      }))
    : { projectId, ...answer };

  const endpoint = answer.length
    ? submitAnswerBatchPath(projectId)
    : submitAnswerPath(projectId);

  return httpAction({
    reducer: `${SURVEY_REDUCER}postAnswer`,
    endpoint,
    verb: 'POST',
    httpClient: SurveyServiceClient(projectId),
    body,
  });
};

export const putAnswer = (projectId, answer) =>
  httpAction({
    reducer: `${SURVEY_REDUCER}putAnswer`,
    endpoint: submitAnswerPath(projectId),
    verb: 'PUT',
    httpClient: SurveyServiceClient(projectId),
    body: { projectId, ...answer },
  });

export const fetchSurveyResponses = (projectId) =>
  httpAction({
    reducer: `${SURVEY_REDUCER}fetchSurveyResponses`,
    endpoint: fetchSurveyResponsesPath(projectId),
    verb: 'GET',
    httpClient: SurveyServiceClient(projectId),
  });

export const resetAnswers = (projectId) =>
  httpAction({
    reducer: `${SURVEY_REDUCER}resetAnswers`,
    endpoint: resetAnswersPath(projectId),
    verb: 'DELETE',
    httpClient: SurveyServiceClient(projectId),
  });

export const postResponses = (projectId, additionalComment) =>
  httpAction({
    reducer: `${SURVEY_REDUCER}postResponses`,
    endpoint: postResponsesPath(projectId),
    verb: 'POST',
    httpClient: SurveyServiceClient(projectId),
    body: { comment: additionalComment },
  });

export const fetchPreviousQuestion = (projectId, orderNumber = null) =>
  httpAction({
    reducer: `${SURVEY_REDUCER}fetchQuestion`,
    endpoint: previousQuestionPath(projectId, orderNumber),
    verb: 'GET',
    httpClient: SurveyServiceClient(projectId),
  });

// Selectors
export const getQuestion = (state) => state.survey.question;
export const getPostAnswerStatus = (state) => state.survey.postAnswerStatus;
export const getSurveyComplete = (state) => state.survey.surveyComplete;
export const getSurveyResponses = (state) => state.survey.surveyResponses;
export const getResetAnswersStatus = (state) => state.survey.resetAnswersStatus;
export const getPostResponsesStatus = (state) =>
  state.survey.postResponsesStatus;
export const getFetchQuestionStatus = (state) =>
  state.survey.fetchQuestionStatus;
export const getFetchSurveyResponsesStatus = (state) =>
  state.survey.fetchSurveyResponsesStatus;
export const getSubmitAnswerStatus = (state) => state.survey.submitAnswerStatus;
export const getSurveyResponsesCompleted = (state) =>
  state.survey.surveyResponses && state.survey.surveyResponses.completed;

export default reducer;
